import {
  addMinutes,
  areIntervalsOverlapping,
  differenceInMinutes,
  format,
  isAfter,
  isBefore,
  isEqual,
  isSameDay,
  isValid,
  parse,
  roundToNearestHours,
  roundToNearestMinutes,
} from 'date-fns';
import { TourDuration } from '@zg-rentals/ts-z-calendar-client';

import type { ZCalendarEvent } from '../constants';
import {
  HOUR_MINUTE_MERIDIEM_FORMAT_STRING,
  TOUR_DURATION_NUMBER_MAP,
  TWENTY_FOUR_HOUR_FORMAT_STRING,
} from '../constants';
import { formatTimeString } from './formatTime';

/**
 * Creates an array of strings representing the times of day from 00:00 to 23:30, incrementing by 30 minutes.
 * The strings are formatted as 12-hour times, with AM/PM suffixes.
 * @returns An array of strings, e.g. ['12:00 AM', '12:30 AM', '1:00 AM', ...]
 */
export const createAvailabilityTimeOptions = (tourDuration: TourDuration, selectedStartTime?: string) => {
  const times = [];

  switch (tourDuration) {
    case TourDuration.MINUTES_15:
      for (let hour = 0; hour < 24; hour++) {
        times.push(`${hour}:00`);
        times.push(`${hour}:15`);
        times.push(`${hour}:30`);
        times.push(`${hour}:45`);
      }
      break;
    case TourDuration.MINUTES_20:
      for (let hour = 0; hour < 24; hour++) {
        times.push(`${hour}:00`);
        times.push(`${hour}:20`);
        times.push(`${hour}:40`);
      }
      break;
    case TourDuration.MINUTES_30:
      for (let hour = 0; hour < 24; hour++) {
        times.push(`${hour}:00`);
        times.push(`${hour}:30`);
      }
      break;
    case TourDuration.MINUTES_60:
      for (let hour = 0; hour < 24; hour++) {
        if (selectedStartTime?.split(':')[1] === '00') {
          times.push(`${hour}:00`);
        } else if (selectedStartTime?.split(':')[1] === '30') {
          times.push(`${hour}:30`);
        } else {
          times.push(`${hour}:00`);
          times.push(`${hour}:30`);
        }
      }
      break;
  }

  return times.map((time) => {
    return formatTimeString(parse(time, TWENTY_FOUR_HOUR_FORMAT_STRING, new Date()), { showMeridiem: true });
  });
};

export const convert12HourTimeTo24HourTime = (time: string) => {
  const date = parse(time, HOUR_MINUTE_MERIDIEM_FORMAT_STRING, new Date());

  if (isValid(date)) {
    return format(date, TWENTY_FOUR_HOUR_FORMAT_STRING);
  }

  return time;
};

export const isDateBeforeOrEqual = (date: Date, dateToCompare: Date) => {
  if (!isValid(date) || !isValid(dateToCompare)) {
    return false;
  }

  return isBefore(date, dateToCompare) || isEqual(date, dateToCompare);
};

export const isTimeBeforeOrEqual = (time: string, timeToCompare: string) => {
  const date = parse(time, HOUR_MINUTE_MERIDIEM_FORMAT_STRING, new Date());
  const dateToCompare = parse(timeToCompare, HOUR_MINUTE_MERIDIEM_FORMAT_STRING, new Date());

  return isDateBeforeOrEqual(date, dateToCompare);
};

export const isOverlappingCalendarAvailability = (
  startDate: Date,
  endDate: Date,
  availabilityEvents: Array<ZCalendarEvent>,
  currentEvent: ZCalendarEvent,
) => {
  return availabilityEvents.some((event) => {
    if (currentEvent.id === event.id) {
      return false;
    }

    return areIntervalsOverlapping({ start: startDate, end: endDate }, { start: event.start, end: event.end });
  });
};

export const isOverlappingTimeSlots = (
  timeSlot1: { start: Date; end: Date },
  timeSlot2: { start: Date; end: Date },
) => {
  // Check if either time slot has an invalid interval (end before start)
  // Invalid intervals cannot overlap with anything, so return false
  if (
    !isValid(timeSlot1.start) ||
    !isValid(timeSlot1.end) ||
    !isValid(timeSlot2.start) ||
    !isValid(timeSlot2.end) ||
    isDateBeforeOrEqual(timeSlot1.end, timeSlot1.start) ||
    isDateBeforeOrEqual(timeSlot2.end, timeSlot2.start)
  ) {
    return false;
  }

  return areIntervalsOverlapping(
    { start: timeSlot1.start, end: timeSlot1.end },
    { start: timeSlot2.start, end: timeSlot2.end },
  );
};

export const isMultipleDayAvailability = (startDate: Date, endDate: Date) => {
  return !isSameDay(startDate, endDate);
};

type CalculateEndTimeWithPreservedDurationParams = {
  originalStart: Date;
  originalEnd: Date;
  newStart: Date;
  targetDate: Date;
  tourDuration: TourDuration;
};

export const calculateEndTimeWithPreservedDuration = ({
  originalStart,
  originalEnd,
  newStart,
  targetDate,
  tourDuration,
}: CalculateEndTimeWithPreservedDurationParams): Date | undefined => {
  if (
    !isValid(originalStart) ||
    !isValid(originalEnd) ||
    !isValid(newStart) ||
    !isValid(targetDate) ||
    isAfter(originalStart, originalEnd) ||
    isBefore(newStart, originalStart)
  ) {
    return undefined;
  }

  const durationMinutes = TOUR_DURATION_NUMBER_MAP[tourDuration];
  const newEndTime = addMinutes(newStart, durationMinutes);

  const isNewEndTimeSameDay = isSameDay(newEndTime, targetDate);

  if (isNewEndTimeSameDay) {
    return newEndTime;
  }

  return undefined;
};

export const getAvailabilityStartTimeByTourDuration = (
  start: Date,
  tourDuration: TourDuration,
  minEventDuration: TourDuration,
) => {
  if (tourDuration !== minEventDuration) {
    if (tourDuration === TourDuration.MINUTES_60) {
      return roundToNearestMinutes(start, {
        nearestTo: TOUR_DURATION_NUMBER_MAP[TourDuration.MINUTES_30],
        roundingMethod: 'ceil',
      });
    }

    return roundToNearestMinutes(start, { nearestTo: TOUR_DURATION_NUMBER_MAP[tourDuration], roundingMethod: 'ceil' });
  }

  return start;
};

export const getAvailabilityEndTimeByTourDuration = (
  start: Date,
  end: Date,
  tourDuration: TourDuration,
  minEventDuration: TourDuration,
) => {
  if (differenceInMinutes(end, start) < TOUR_DURATION_NUMBER_MAP[tourDuration]) {
    return addMinutes(start, TOUR_DURATION_NUMBER_MAP[tourDuration]);
  }

  if (tourDuration !== minEventDuration) {
    if (tourDuration === TourDuration.MINUTES_60) {
      const nearestHour = roundToNearestHours(end, { roundingMethod: 'floor' });

      return start.getMinutes() === 0
        ? nearestHour
        : addMinutes(nearestHour, TOUR_DURATION_NUMBER_MAP[TourDuration.MINUTES_30]);
    }

    return roundToNearestMinutes(end, { nearestTo: TOUR_DURATION_NUMBER_MAP[tourDuration], roundingMethod: 'floor' });
  }

  return end;
};

export const isStartAndEndTimeFullHourBlock = (start: Date, end: Date, tourDuration: TourDuration) => {
  return differenceInMinutes(end, start) % TOUR_DURATION_NUMBER_MAP[tourDuration] === 0;
};
